import type { PrefetchContext, PrefetchedDataProps } from 'src/shared/prefetch';

import { useQueryString } from 'src/app/shared/hooks/location';
import type { MergedState } from 'src/app/shared/hooks/state';
import { useMergedStates } from 'src/app/shared/hooks/state';
import { getQueryStringSingleValue } from 'src/app/shared/utils/query';

import { prefetchPsfPromoteOrSharePageDataFromApi, usePsfPromoteOrSharePageDataFromApi } from './pageData';
import { prefetchPsfPromoteOrSharePageFcm, useAsyncPsfPromoteOrSharePageFcm } from './pageFcm';
import {
	prefetchPsfPromoteOrSharePageFcmExperiments,
	useAsyncPsfPromoteOrSharePageFcmExperiments,
} from './pageFcmExperiments';

export type PsfPromoteOrSharePagePrefetchedContext = Readonly<{
	pageData: Parameters<typeof usePsfPromoteOrSharePageDataFromApi>[0];
}>;

export type PsfPromoteOrSharePageCombinedState = MergedState<
	ReturnType<typeof usePsfPromoteOrSharePageDataFromApi>,
	ReturnType<typeof useAsyncPsfPromoteOrSharePageFcmExperiments>,
	ReturnType<typeof useAsyncPsfPromoteOrSharePageFcm>
>;

export function usePageContext(
	slug: string,
	prefetchedData?: PsfPromoteOrSharePagePrefetchedContext,
): PsfPromoteOrSharePageCombinedState {
	const query = useQueryString();
	return useMergedStates(
		usePsfPromoteOrSharePageDataFromApi(prefetchedData?.pageData, slug, {
			recruiterId: getQueryStringSingleValue(query, 'recruiter'),
			banditOverrides: {
				combo: getQueryStringSingleValue(query, 'bo.combo'),
			},
		}),
		useAsyncPsfPromoteOrSharePageFcmExperiments(),
		useAsyncPsfPromoteOrSharePageFcm(),
	);
}

export async function prefetchPageContext(
	slug: string,
	context: PrefetchContext,
): Promise<PsfPromoteOrSharePagePrefetchedContext> {
	const [pageData] = await Promise.all([
		prefetchPsfPromoteOrSharePageDataFromApi(context, slug, {
			recruiterId: getQueryStringSingleValue(context.query, 'recruiter'),
			banditOverrides: {
				combo: getQueryStringSingleValue(context.query, 'bo.combo'),
			},
		}),
		prefetchPsfPromoteOrSharePageFcmExperiments(context),
		prefetchPsfPromoteOrSharePageFcm(context),
	]);

	return { pageData };
}

export type PsfPromoteOrSharePagePrefetchedDataProps = PrefetchedDataProps<PsfPromoteOrSharePagePrefetchedContext>;
